import { useLocale } from 'use-intl';
import { useTranslations } from '@/i18n/translations';
import { Body, CalloutBar, I, Stack } from '@leanin/ui';
import type { LeaderRemovalView } from './leader-removal-reasons';

// The author's view of a leader removal on their own post, comment or chat
// message: which group, why, the leader's note, and (posts) where it is
// still visible. Grey, not a warning colour: the content is gone from the
// group, the author is not in trouble. The
// server sends `removals` / `removal` to the author only, so rendering this
// never needs a viewer check of its own. The reason is the notification
// row's own label, so the banner and the notification read identically.
export function LeaderRemovalBanner({
  removal,
  contentKind,
  stillVisibleIn,
  density = 'card',
}: {
  removal: LeaderRemovalView;
  contentKind: 'post' | 'comment' | 'message';
  /** Names of the post's other groups, which still show it. Posts only. */
  stillVisibleIn?: ReadonlyArray<string>;
  /** `card`: a full-bleed strip across the top of a post card, like the
   *  pinned / featured bars. `inline`: a tighter, borderless strip in place
   *  of a comment or chat bubble. */
  density?: 'card' | 'inline';
}) {
  const t = useTranslations('Moderation');
  const tn = useTranslations('Notifications');
  const locale = useLocale();
  const reason = tn(`kinds.contentRemoved.reasons.${removal.reason}`);
  return (
    <CalloutBar
      tone="neutral"
      icon={I.shield}
      align="start"
      {...(density === 'inline'
        ? {
            bordered: false,
            padX: 'md' as const,
            padY: 'sm' as const,
            style: { borderRadius: 'var(--radius-md)' },
          }
        : {})}
      label={
        <Stack as="span" gap="xs">
          <span>
            {t(`leaderRemoval.banner.${contentKind}`, {
              group: removal.groupName,
              reason,
            })}
          </span>
          {removal.note !== null ? (
            <Body as="span" size="sm">
              {t('leaderRemoval.banner.note', { note: removal.note })}
            </Body>
          ) : null}
          {stillVisibleIn !== undefined && stillVisibleIn.length > 0 ? (
            <Body as="span" size="sm">
              {t('leaderRemoval.banner.stillVisible', {
                groups: new Intl.ListFormat(locale, { type: 'conjunction' }).format(
                  stillVisibleIn,
                ),
              })}
            </Body>
          ) : null}
        </Stack>
      }
    />
  );
}
